/**
 * ShiftCover365 — Commercial Model (frontend source of truth).
 *
 * Mirror of /app/backend/pricing.py — update BOTH files together or the
 * UI will drift from what Stripe actually charges.
 *
 * Feb 2026 Pilot Readiness Sprint. The old "£9 Founding Partner pilot"
 * is gone. Any code that imports from this file uses the ONE canonical
 * commercial model.
 */

export const TRIAL_DAYS = 14;
export const TRIAL_REQUIRES_PAYMENT_METHOD = false;
export const DEFAULT_POST_TRIAL_PLAN = 'payg';
export const CURRENCY = 'gbp';

// Canonical PAYG billing phrase — mirror of PAYG_HEADLINE_PHRASE in
// /app/backend/pricing.py. Use this constant every time PAYG is described
// so marketing, pricing, FAQ and receipts never drift.
export const PAYG_HEADLINE_PHRASE = '£3.99 per successfully completed shift, billed monthly';

// -- Plans (pence-precise) --------------------------------------------
export const PLANS = {
  payg: {
    code: 'payg',
    name: 'Pay As You Go',
    tagline: 'Only pay for shifts you actually cover.',
    interval: null,
    amountPence: 399,             // £3.99
    unitLabel: 'per successfully completed shift',
    maxLocations: 999,
    isMetered: true,
    lookupKey: 'shiftcover365_payg_per_shift',
    testid: 'plan-payg',
  },
  monthly: {
    code: 'monthly',
    name: 'Monthly',
    tagline: 'Great for a single busy location.',
    interval: 'month',
    amountPence: 2999,            // £29.99
    unitLabel: 'per location per month',
    maxLocations: 1,
    isMetered: false,
    lookupKey: 'shiftcover365_monthly_per_location',
    testid: 'plan-monthly',
  },
  annual: {
    code: 'annual',
    name: 'Annual',
    tagline: 'Two months free vs Monthly.',
    interval: 'year',
    amountPence: 29000,           // £290
    unitLabel: 'per location per year',
    maxLocations: 1,
    isMetered: false,
    lookupKey: 'shiftcover365_annual_per_location',
    testid: 'plan-annual',
  },
  multisite: {
    code: 'multisite',
    name: 'Multi-site',
    tagline: 'Up to 5 locations, one flat fee.',
    interval: 'month',
    amountPence: 9900,            // £99
    unitLabel: 'per month, up to 5 locations',
    maxLocations: 5,
    isMetered: false,
    lookupKey: 'shiftcover365_multisite_monthly',
    testid: 'plan-multisite',
  },
};

// -- Founding Member offer --------------------------------------------
export const FOUNDING_MEMBER_ENABLED = true;
export const FOUNDING_MEMBER_CAP = 20;
export const FOUNDING_MEMBER_PLAN = 'monthly';
export const FOUNDING_MEMBER_PRICE_PENCE = 1999;   // £19.99/mo
export const FOUNDING_MEMBER_COUPON_ID = 'founding_member_10_off_forever';

// -- Formatting helpers -----------------------------------------------
export function displayPrice(pence) {
  if (pence == null) return '';
  const p = pence % 100;
  const pounds = Math.floor(pence / 100);
  if (p === 0) return `£${pounds}`;
  return `£${pounds}.${String(p).padStart(2, '0')}`;
}

export function planDisplayPrice(planCode) {
  const plan = PLANS[planCode];
  if (!plan) return '';
  return displayPrice(plan.amountPence);
}

export function planIntervalLabel(planCode) {
  const plan = PLANS[planCode];
  if (!plan) return '';
  if (plan.isMetered) return plan.unitLabel;
  return `/${plan.interval}`;
}

/** Rough £/mo equivalent for the Annual plan, for savings badges. */
export function annualMonthlyEquivalent() {
  return displayPrice(Math.round(PLANS.annual.amountPence / 12));
}
